import { useEffect, useState, useSyncExternalStore } from 'react';
import { api, getSession, onSession } from './api';

/** The signed-in session (or null), kept in step with sign-in, refresh and sign-out. */
export const useSession = () => useSyncExternalStore(onSession, getSession);

/** A small loader: data, error and a reload, re-run when `deps` change. */
export function useLoad<T>(load: () => Promise<T>, deps: unknown[]) {
  const [state, setState] = useState<{ data: T | null; error: unknown; loading: boolean }>({ data: null, error: null, loading: true });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    setState((s) => ({ ...s, loading: true }));
    load().then(
      (data) => live && setState({ data, error: null, loading: false }),
      (error: unknown) => live && setState({ data: null, error, loading: false }),
    );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  return { ...state, reload: () => setTick((n) => n + 1) };
}

/** The live config's shop switch, fetched once per page load. */
let shopEntry: Promise<boolean> | null = null;

/** Whether the site links to its shop (live config `webShopEntry`); off until the config says so. */
export function useShopEntry(): boolean {
  const [on, setOn] = useState(false);
  useEffect(() => {
    let live = true;
    // A failed fetch keeps the links hidden and tries again on the next page.
    shopEntry ??= api.config().then(
      (c) => c.webShopEntry === true,
      () => ((shopEntry = null), false),
    );
    void shopEntry.then((v) => live && setOn(v));
    return () => {
      live = false;
    };
  }, []);
  return on;
}
